import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import DiscountsCard from "./DiscountsCard";
import SourcesCard from "./SourcesCard";

const date = (v) => (v ? new Date(typeof v === "number" ? v * 1000 : v).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "-");

function Detail({ id, onClose }) {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get(`/admin/store-customers/${id}`).then((r) => { setD(r.data); setNotes(r.data.notes); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="p-6 mono text-xs text-[#64748B]">Loading…</div>;
  const c = d.customer;
  const save = async () => { await api.post(`/admin/store-customers/${id}/notes`, { notes }); setMsg("Notes saved"); };
  const reset = async () => {
    if (!window.confirm("Unlink all MT5 accounts for this customer? Their bots will re-link on next start.")) return;
    await api.post(`/admin/store-customers/${id}/reset-accounts`); setMsg("Accounts reset"); load();
  };
  return (
    <div data-testid="customer-detail" className="p-6 space-y-5">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xl font-medium">{c.name || "-"} · {c.email}</div>
          <div className="mono text-[11px] text-[#94A3B8] mt-1">Joined {date(c.created_at)} · licence {c.license_key}</div>
        </div>
        <button onClick={onClose} data-testid="customer-detail-close" className="mono text-[10px] uppercase px-3 py-1.5 rounded-full border border-blue-900/60">Close</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className="mono text-[9px] uppercase text-[#64748B]">Plan</div>{c.subscription.tier_name || "none"} ({c.subscription.status || "-"})</div>
        <div><div className="mono text-[9px] uppercase text-[#64748B]">Renews / ends</div>{date(c.subscription.current_period_end)}{c.subscription.cancel_at_period_end ? " (cancelled)" : ""}</div>
        <div><div className="mono text-[9px] uppercase text-[#64748B]">Marketing</div>{c.marketing_opt_in ? "opted in" : "no"}</div>
        <div>{d.stripe_link && <a href={d.stripe_link} target="_blank" rel="noreferrer" data-testid="customer-stripe-link" className="text-[#FBBF24] underline">Open in Stripe (refunds)</a>}</div>
      </div>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#38BDF8]">Picked bots & linked MT5 accounts</div>
        {c.picks.map((p) => (
          <div key={p.store_id} className="text-sm mt-1">{p.name || p.store_id} - {p.accounts.map((a) => `${a.account} (${a.demo ? "demo" : "live"}, seen ${date(a.last_seen)})`).join(", ") || "no accounts yet"}</div>
        ))}
        <button onClick={async () => { await api.post(`/admin/store-customers/${id}/unlimited-swaps`, { on: !c.unlimited_swaps }); load(); }}
          data-testid="customer-unlimited-swaps" className="mt-2 mr-2 mono text-[10px] uppercase px-3 py-1.5 rounded-full border border-[#34D399]/60 text-[#34D399]">
          {c.unlimited_swaps ? "Unlimited bot swaps: ON (turn off)" : "Allow unlimited bot swaps"}
        </button>
        <button onClick={reset} data-testid="customer-reset-accounts" className="mt-2 mono text-[10px] uppercase px-3 py-1.5 rounded-full border border-[#F87171]/60 text-[#F87171]">Reset linked accounts</button>
      </div>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#38BDF8]">Payments & plan changes</div>
        {d.payments.map((p) => <div key={p.session_id} className="text-xs mt-1 mono">{date(p.created_at)} · {p.tier} · £{p.amount} · {p.payment_status}</div>)}
        {d.plan_changes.map((p, i) => <div key={i} className="text-xs mt-1 mono text-[#FBBF24]">{date(p.at)} · {p.from} → {p.to} ({p.upgrade ? "upgrade" : "downgrade"})</div>)}
      </div>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#38BDF8]">Live results (all time) · {d.performance.mode}</div>
        <div className="text-sm mt-1">Total {d.performance.total_net} {d.performance.currency || ""} · {d.performance.reporting} bots reporting</div>
      </div>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#38BDF8]">Recent licence checks</div>
        <div className="max-h-40 overflow-y-auto">
          {d.license_checks.map((x, i) => <div key={i} className="text-[11px] mono text-[#94A3B8]">{date(x.ts)} · {x.product} · {x.account} · {x.ok ? "OK" : x.reason || "refused"}</div>)}
        </div>
      </div>
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#38BDF8]">Admin notes (refunds, support)</div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="customer-notes"
          className="w-full mt-1 bg-[#0B0D14] border border-[#1F2937] rounded p-2 text-sm" />
        <button onClick={save} data-testid="customer-notes-save" className="mt-1 mono text-[10px] uppercase px-3 py-1.5 rounded-full bg-[#FBBF24] text-[#050914]">Save notes</button>
        {msg && <span className="ml-3 text-xs text-[#34D399]">{msg}</span>}
      </div>
    </div>
  );
}

export default function StoreCustomers() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => { const t = setTimeout(() => api.get("/admin/store-customers", { params: { q } }).then((r) => setD(r.data)), 250); return () => clearTimeout(t); }, [q]);
  const t = d?.totals;
  return (
    <div data-testid="store-customers-page" className="min-h-screen bg-[#050914] text-[#F1F5F9]">
      <div className="max-w-[1300px] mx-auto px-6 py-10">
        <Link to="/admin-mt5" data-testid="customers-back" className="mono text-[10px] uppercase tracking-widest text-[#94A3B8]">← MT5 Lab</Link>
        <h1 className="text-3xl font-medium mt-3">Store customers</h1>
        <SourcesCard />
        <DiscountsCard />
        {t && (
          <div className="flex flex-wrap gap-4 mt-4 mono text-xs" data-testid="customers-totals">
            <span>{t.all} accounts</span><span className="text-[#34D399]">{t.active} paying</span>
            <span className="text-[#FBBF24]">£{t.mrr_gbp}/mo MRR</span>
            <span className="text-[#94A3B8]">single {t.by_tier.single} · five {t.by_tier.five} · all {t.by_tier.all}</span>
          </div>
        )}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" data-testid="customers-search"
          className="mt-5 w-full max-w-sm bg-[#0B0D14] border border-[#1F2937] rounded px-3 py-2 text-sm" />
        <div className="mt-4 overflow-x-auto border border-blue-900/40 rounded-lg">
          <table className="w-full text-sm">
            <thead className="mono text-[10px] uppercase tracking-widest text-[#64748B] bg-[#050B18]">
              <tr>{["Email", "Name", "Plan", "Status", "Renews", "Bots", "MT5 accts", "Joined"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(d?.customers || []).map((c) => (
                <tr key={c.id} onClick={() => setOpen(c.id)} data-testid={`customer-row-${c.email}`} className="border-t border-blue-900/20 hover:bg-blue-900/10 cursor-pointer">
                  <td className="px-3 py-2">{c.email}</td><td className="px-3 py-2">{c.name || "-"}</td>
                  <td className="px-3 py-2">{c.tier_name || "-"}</td>
                  <td className={`px-3 py-2 ${c.active ? "text-[#34D399]" : "text-[#64748B]"}`}>{c.active ? (c.cancel_at_period_end ? "cancelling" : "active") : c.status || "no plan"}</td>
                  <td className="px-3 py-2 text-xs">{date(c.renews)}</td><td className="px-3 py-2">{c.picks}</td><td className="px-3 py-2">{c.accounts}</td>
                  <td className="px-3 py-2 text-xs">{date(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" onClick={() => setOpen(null)}>
          <div className="w-full max-w-3xl bg-[#0B1220] border border-blue-900/60 rounded-xl" onClick={(e) => e.stopPropagation()}>
            <Detail id={open} onClose={() => setOpen(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
